import { forwardRef, useMemo } from 'react';

import { NumberInputProps, NumberInput } from '@any-ui-react/core';
import { useUncontrolled } from '@mantine/hooks';

import { CurrencyUtils, LanguageUtils } from '@anyx/shared-utils';


interface InputPriceProps extends Omit<NumberInputProps, 'defaultValue' | 'value' | 'onChange'> {
  currency?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (e: string) => void;
}

const useFormatPrice = ({ currency }: { currency: string }) => {
  const intlFormatter = useMemo(() => {
    return new Intl.NumberFormat(LanguageUtils.getTwoLettersCode(LanguageUtils.DEFAULT), {
      style: 'currency',
      currency,
    });
  }, [currency]);

  const [thousandsSeparator, decimalSeparator] = useMemo(() => {
    const parts = intlFormatter.formatToParts(1000);

    const thousandsSeparator = parts.find((part) => part.type === 'group')?.value;
    const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value;

    return [thousandsSeparator || ',', decimalSeparator || '.'];
  }, [intlFormatter]);

  return { thousandsSeparator, decimalSeparator };
};

export const InputPrice = forwardRef<HTMLInputElement, InputPriceProps>(
  (
    {
      value,
      defaultValue,
      currency = CurrencyUtils.DEFAULT,
      onChange,
      classNames,
      ...rest
    }: InputPriceProps,
    ref
  ) => {
    const [_value, handleChange] = useUncontrolled<string>({
      value,
      defaultValue,
      finalValue: '',
      onChange,
    });

    const { thousandsSeparator, decimalSeparator } = useFormatPrice({
      currency,
    });

    return (
      <NumberInput
        ref={ref}
        classNames={{
          ...classNames,
          section: 'w-max',
        }}
        rightSection={<span className="border-gray-2 border-l pr-2 pl-1">{currency}</span>}
        rightSectionWidth={48}
        min={0}
        thousandSeparator={thousandsSeparator}
        decimalSeparator={decimalSeparator}
        decimalScale={CurrencyUtils.getRelevantDecimals(currency)}
        value={_value ? Number(_value) : ''}
        onChange={(value) => {
          handleChange(value.toString());
        }}
        {...rest}
      />
    );
  }
);
